import {
  GridRenderEditCellParams,
  useGridApiContext,
} from '@mui/x-data-grid-premium'
import { GridValidRowModel } from '@mui/x-data-grid-pro'
import TextInput, { TextInputRef } from 'ext-ui/components/TextInput/TextInput'
import { LoadingState } from 'ext-ui/hooks/useLoadingStateAnimation'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import { useTranslate } from 'src/localization/Trns'

import styles from './TextFieldCell.style'

interface TextFieldCellProps<Row extends GridValidRowModel> {
  characterLimit?: number
  tooltip?: string
  params: GridRenderEditCellParams<Row, string>
  editMode: 'row' | 'cell'
  errorText?: React.ReactElement | string
  clearError?: () => void
  setValue?: (value: string) => void
  placeholder?: string
  microcopy?: string
  loadingState?: LoadingState
  disabled?: boolean
  className?: string
}

const TextFieldCell = <Row extends GridValidRowModel>(
  props: TextFieldCellProps<Row>,
): React.ReactElement => {
  const translate = useTranslate()
  const [showCharacterLimitWarning, setShowCharacterLimitWarning] =
    useState<boolean>(false)

  const showError =
    props.errorText !== undefined ||
    (props.characterLimit !== undefined && showCharacterLimitWarning)

  const ref = useRef<TextInputRef>(null)
  const apiRef = useGridApiContext()

  useEffect(() => {
    // initialize form fields
    const initialValue = props.params.value || props.params.formattedValue || ''
    apiRef.current.setEditCellValue({
      id: props.params.id,
      field: props.params.field,
      value: initialValue,
    })
    props.setValue?.(initialValue)
    props.clearError?.()
  }, [])

  useLayoutEffect(() => {
    if (props.params.hasFocus) {
      ref.current?.focus()
    } else {
      // stop editing and discard changes when focus leaves this component
      if (props.editMode === 'row') {
        apiRef.current.stopRowEditMode({
          id: props.params.id,
          ignoreModifications: true,
        })
      } else {
        apiRef.current.stopCellEditMode({
          id: props.params.id,
          field: props.params.field,
          ignoreModifications: true,
        })
      }
    }
  }, [props.params.hasFocus])

  const handleValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    props.clearError?.()
    // show/hide character limit
    if (props.characterLimit !== undefined) {
      if (event.target.value.length > props.characterLimit) {
        setShowCharacterLimitWarning(true)
      } else {
        setShowCharacterLimitWarning(false)
      }
    }
    // update value
    apiRef.current.setEditCellValue({
      id: props.params.id,
      field: props.params.field,
      value: event.target.value,
    })
    props.setValue?.(event.target.value)
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === 'Enter') {
      // stop editing on Enter key press
      if (props.editMode === 'row') {
        apiRef.current.stopRowEditMode({
          id: props.params.id,
        })
      } else {
        apiRef.current.stopCellEditMode({
          id: props.params.id,
          field: props.params.field,
        })
      }
    }
  }

  const errorText = showError
    ? props.characterLimit && showCharacterLimitWarning
      ? translate('${characterLimit} character limit reached', {
          characterLimit: props.characterLimit.toString(),
        })
      : props.errorText
    : undefined

  return (
    <DataGridCellWrapper
      params={props.params}
      className={props.className}
      css={styles}
    >
      <TextInput
        className={'TextInput'}
        variant={'small'}
        ref={ref}
        tooltipProps={{
          children: props.tooltip,
        }}
        value={props.params.value ?? ''}
        placeholder={props.placeholder}
        onChange={handleValueChange}
        onKeyDown={handleKeyDown}
        loadingState={props.loadingState}
        disabled={props.disabled}
        microcopy={props.microcopy}
        errorText={errorText}
      />
    </DataGridCellWrapper>
  )
}

export default React.memo(TextFieldCell)
